import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usersAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const UserProfilePage = () => {
  const { user, refreshUser, updateUserLocal } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    telefone: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cpf: '',
    rg: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await usersAPI.getProfile();
      const initial = {
        nome: data.nome || '',
        telefone: data.telefone || '',
        cep: data.cep || '',
        endereco: data.endereco || '',
        numero: data.numero || '',
        complemento: data.complemento || '',
        bairro: data.bairro || '',
        cidade: data.cidade || '',
        estado: data.estado || '',
        cpf: data.cpf || '',
        rg: data.rg || '',
      };
      setForm(initial);
      setSnapshot(initial);
      updateUserLocal(data);
    } catch {
      toast.error('Não foi possível carregar o perfil.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCepBlur = async () => {
    const cep = form.cep.replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm((f) => ({
          ...f,
          endereco: data.logradouro || f.endereco,
          bairro: data.bairro || f.bairro,
          cidade: data.localidade || f.cidade,
          estado: data.uf || f.estado,
        }));
      }
    } catch {
      /* ignore */
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await usersAPI.uploadProfilePicture(fd);
      updateUserLocal({ profile_picture_url: data.profile_picture_url || data.url });
      toast.success('Foto atualizada.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar foto.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersAPI.updateProfile(form);
      await refreshUser();
      setSnapshot({ ...form });
      toast.success('Perfil salvo.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar perfil.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (snapshot) setForm({ ...snapshot });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48 bg-white/10" />
        <Skeleton className="h-64 w-full bg-white/10" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Meu perfil</h1>

      <form
        onSubmit={handleSave}
        className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 space-y-6"
      >
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border border-white/20">
            <AvatarImage src={user?.profile_picture_url} alt={user?.nome} />
            <AvatarFallback className="bg-[#0E76FF]/30">
              {(user?.nome || '?').slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <input
              id="photo"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhoto}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploadingPhoto}
              onClick={() => document.getElementById('photo')?.click()}
            >
              {uploadingPhoto ? 'Enviando…' : 'Alterar foto'}
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div className="space-y-2">
            <Label>Telefone</Label>
            <Input
              value={form.telefone}
              onChange={(e) => setForm({ ...form, telefone: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div className="space-y-2">
            <Label>CEP</Label>
            <Input
              value={form.cep}
              onChange={(e) => setForm({ ...form, cep: e.target.value })}
              onBlur={handleCepBlur}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Endereço</Label>
            <Input
              value={form.endereco}
              onChange={(e) => setForm({ ...form, endereco: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div className="space-y-2">
            <Label>Número</Label>
            <Input
              value={form.numero}
              onChange={(e) => setForm({ ...form, numero: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div className="space-y-2">
            <Label>Complemento</Label>
            <Input
              value={form.complemento}
              onChange={(e) => setForm({ ...form, complemento: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div className="space-y-2">
            <Label>Bairro</Label>
            <Input
              value={form.bairro}
              onChange={(e) => setForm({ ...form, bairro: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div className="space-y-2">
            <Label>Cidade</Label>
            <Input
              value={form.cidade}
              onChange={(e) => setForm({ ...form, cidade: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          <div className="space-y-2">
            <Label>Estado</Label>
            <Input
              value={form.estado}
              onChange={(e) => setForm({ ...form, estado: e.target.value })}
              className="bg-white/5 border-white/20"
            />
          </div>
          {user?.role === 'videomaker' && (
            <>
              <div className="space-y-2">
                <Label>CPF</Label>
                <Input
                  value={form.cpf}
                  onChange={(e) => setForm({ ...form, cpf: e.target.value })}
                  className="bg-white/5 border-white/20"
                />
              </div>
              <div className="space-y-2">
                <Label>RG</Label>
                <Input
                  value={form.rg}
                  onChange={(e) => setForm({ ...form, rg: e.target.value })}
                  className="bg-white/5 border-white/20"
                />
              </div>
            </>
          )}
        </div>

        <p className="text-xs text-white/50">
          Dados tratados conforme a{' '}
          <Link to="/politica-de-privacidade" className="underline text-[#0E76FF]">
            Política de Privacidade
          </Link>
          .
        </p>

        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={saving} className="bg-[#0E76FF] hover:bg-[#2563EB]">
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
          <Button type="button" variant="outline" onClick={handleCancel} className="border-white/20">
            Cancelar alterações
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserProfilePage;
